/**
 * publicFormShared — pure helpers + types shared by the two public form
 * surfaces at /f/:token: the one-question-per-screen stepper (landing forms,
 * PublicForm.tsx) and the full scrollable page (study questionnaires,
 * QuestionnaireForm.tsx).
 *
 * Kept dependency-free (no React) so both surfaces evaluate show_if branching
 * and resume state identically — the visibility/equality semantics MUST match
 * the server's crmForms.js (looseEq / isQuestionVisible) verbatim.
 */

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
/** Local resume cache TTL — WCR parity (24h). */
export const LOCAL_TTL_MS = 24 * 60 * 60 * 1000;

export interface PublicQuestion {
  key: string;
  type: string;
  label: string;
  required: boolean;
  description?: string | null;
  placeholder?: string | null;
  options?: { value: string; label: string }[] | null;
  min?: number | null;
  max?: number | null;
  show_if?: ShowIfRule | null;
}

/** One branching condition. `in`/`not_in` carry a list; the rest a scalar. */
export interface ShowIfCondition { question_key: string; operator: string; value: any }
/** Two or more conditions combined with any (OR) / all (AND). */
export interface ShowIfGroup { match: 'any' | 'all'; conditions: ShowIfCondition[] }
export type ShowIfRule = ShowIfCondition | ShowIfGroup;

/** Either stored shape → one { match, conditions } form. Mirrors
 * normalizeShowIf in the server's crmForms.js — the two MUST stay in sync. */
export function normalizeShowIf(rule: ShowIfRule | null | undefined): ShowIfGroup | null {
  if (!rule || typeof rule !== 'object') return null;
  const group = rule as ShowIfGroup;
  if (Array.isArray(group.conditions)) {
    const conditions = group.conditions.filter((c) => c && typeof c.question_key === 'string');
    if (conditions.length === 0) return null;
    return { match: group.match === 'all' ? 'all' : 'any', conditions };
  }
  const single = rule as ShowIfCondition;
  if (typeof single.question_key !== 'string') return null;
  return { match: 'all', conditions: [single] };
}

/** Exact list membership for `in`/`not_in`. Both sides string-coerce (option
 * values are strings, number/scale answers are real numbers) and a
 * multiple_choice answer is an ARRAY — a match is a non-empty intersection,
 * i.e. "they picked A or B or C". */
const inList = (answer: any, value: any) => {
  const vals = (Array.isArray(value) ? value : [value]).map(String);
  if (Array.isArray(answer)) return answer.some((a) => vals.includes(String(a)));
  return vals.includes(String(answer));
};

/** One condition against an ANSWERED value. Mirrors showIfMatches in the
 * server's crmForms.js — the two MUST stay in sync. */
export function matchesShowIf(answer: any, operator: string, value: any): boolean {
  switch (operator) {
    case 'equals': return looseEq(answer, value);
    case 'not_equals': return !looseEq(answer, value);
    case 'contains':
      // Verbatim WCR: arrays comma-join through String(), so substring
      // conditions on multiple_choice answers match like the old site.
      return String(answer).includes(String(value));
    case 'greater_than': return Number(answer) > Number(value);
    case 'less_than': return Number(answer) < Number(value);
    case 'in': return inList(answer, value);
    case 'not_in': return !inList(answer, value);
    default: return false;
  }
}

/** WCR stored every widget answer as a string; our renderer emits real
 * numbers. equals/not_equals therefore compare with string-coercion tolerance
 * so imported conditions like equals "5" keep matching. Mirrors the server's
 * looseEq in crmForms.js — the two MUST stay in sync. */
export const looseEq = (a: any, b: any) => a === b || String(a) === String(b);

/** WCR visibleQuestions semantics: hidden while the referenced answer is
 * unanswered. null = locally CLEARED (the server deletes the row), so it
 * counts as unanswered too — otherwise the client would evaluate
 * Number(null)=0 against conditions the server sees as undefined. */
export function isVisible(q: { show_if?: ShowIfRule | null }, values: Record<string, any>): boolean {
  const norm = normalizeShowIf(q.show_if);
  if (!norm) return true;
  const test = (cond: ShowIfCondition) => {
    const answer = values[cond.question_key];
    if (answer === undefined || answer === null) return false;
    return matchesShowIf(answer, cond.operator, cond.value);
  };
  // 'all' = every condition holds; 'any' = at least one does. An unanswered
  // source makes ITS condition false either way, so a single rule keeps the
  // exact WCR behavior.
  return norm.match === 'all' ? norm.conditions.every(test) : norm.conditions.some(test);
}

export const hasValue = (v: any) =>
  v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

export function loadLocal(token: string): Record<string, any> {
  try {
    const raw = window.localStorage.getItem(`crm-f-${token}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Date.now() - (parsed.at || 0) > LOCAL_TTL_MS) return {};
    return parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : {};
  } catch {
    return {};
  }
}

export function saveLocal(token: string, answers: Record<string, any>) {
  try {
    window.localStorage.setItem(`crm-f-${token}`, JSON.stringify({ at: Date.now(), answers }));
  } catch {
    // Private mode / quota — resume just degrades to the server's answered keys.
  }
}

export function clearLocal(token: string) {
  try {
    window.localStorage.removeItem(`crm-f-${token}`);
  } catch {
    /* noop */
  }
}
